import { collection, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { db } from '@/services';
import type { AssignmentSlot, RouteAssignment } from '@/types';
import { notifyLiveDataError } from '@/utils';
import { createStartupSnapshotLogger, logStartupTiming } from '@/utils/startupPerformance';

/**
 * Subscribes to every standard/Extra assignment and every exclusive route
 * slot for one calendar date.
 */
export const useDailyAssignments = (date: string) => {
  const [assignments, setAssignments] = useState<RouteAssignment[]>([]);
  const [slotsByRouteId, setSlotsByRouteId] = useState<Record<string, AssignmentSlot>>({});
  const [loading, setLoading] = useState(true);
  const logAssignmentsSnapshot = useMemo(
    () => createStartupSnapshotLogger('daily-assignments'),
    [],
  );
  const logSlotsSnapshot = useMemo(() => createStartupSnapshotLogger('assignment-slots'), []);

  useEffect(() => {
    const startedAt = performance.now();
    setLoading(true);
    setAssignments([]);
    setSlotsByRouteId({});

    let assignmentsLoaded = false;
    let slotsLoaded = false;
    const maybeFinishLoading = () => {
      if (assignmentsLoaded && slotsLoaded) setLoading(false);
    };

    const assignmentsQuery = query(
      collection(db, 'assignments'),
      where('date', '==', date),
      orderBy('assignedAt', 'desc'),
    );
    const unsubAssignments = onSnapshot(
      assignmentsQuery,
      (snapshot) => {
        logAssignmentsSnapshot({
          fromCache: snapshot.metadata.fromCache,
          resultCount: snapshot.size,
        });
        setAssignments(
          snapshot.docs.map((docSnap) => ({
            ...(docSnap.data() as RouteAssignment),
            id: docSnap.id,
          })),
        );
        assignmentsLoaded = true;
        maybeFinishLoading();
      },
      (error) => {
        logStartupTiming('daily-assignments first-snapshot-error', startedAt, { failed: true });
        notifyLiveDataError('useDailyAssignments (assignments)', error);
        assignmentsLoaded = true;
        maybeFinishLoading();
      },
    );

    const slotsQuery = query(collection(db, 'assignmentSlots'), where('date', '==', date));
    const unsubSlots = onSnapshot(
      slotsQuery,
      (snapshot) => {
        logSlotsSnapshot({
          fromCache: snapshot.metadata.fromCache,
          resultCount: snapshot.size,
        });
        const next: Record<string, AssignmentSlot> = {};
        for (const docSnap of snapshot.docs) {
          const slot = docSnap.data() as AssignmentSlot;
          next[slot.routeId] = slot;
        }
        setSlotsByRouteId(next);
        slotsLoaded = true;
        maybeFinishLoading();
      },
      (error) => {
        logStartupTiming('assignment-slots first-snapshot-error', startedAt, { failed: true });
        notifyLiveDataError('useDailyAssignments (slots)', error);
        slotsLoaded = true;
        maybeFinishLoading();
      },
    );

    return () => {
      unsubAssignments();
      unsubSlots();
    };
  }, [date, logAssignmentsSnapshot, logSlotsSnapshot]);

  return { assignments, slotsByRouteId, loading };
};
